import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can, type TestDataClearedDto, type TestDataDto } from '@pl/domain';
import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { zonedDateTime } from '../../lib/format';
import { qk, useMe, useOrg } from '../../lib/queries';
import { Empty, ErrorNote, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { Modal } from '../../ui/Dialog';
import s from './SettingsGoLive.module.css';

/** What goes, in the order people think of it. Zero lines are left out. */
const REMOVED: [keyof TestDataDto['remove'], string, string][] = [
  ['sampleProducts', 'sample product', 'sample products'],
  ['otherProducts', 'other product (imported or added by hand), which will also be removed', 'other products (imported or added by hand), which will also be removed'],
  ['boxes', 'box', 'boxes'],
  ['stickerBatches', 'sticker batch', 'sticker batches'],
  ['pallets', 'pallet', 'pallets'],
  ['orders', 'order', 'orders'],
  ['transfers', 'transfer', 'transfers'],
  ['counts', 'count', 'counts'],
  ['discrepancies', 'discrepancy', 'discrepancies'],
  ['imports', 'import', 'imports'],
  ['scans', 'scan', 'scans'],
  ['events', 'history entry', 'history entries'],
  ['assistantConversations', 'assistant conversation', 'assistant conversations'],
  ['assistantWords', 'assistant product word', 'assistant product words'],
  ['demoStaff', 'demo staff account', 'demo staff accounts'],
];
const KEPT: [keyof TestDataDto['keep'], string, string][] = [
  ['sites', 'site', 'sites'],
  ['people', 'person who signs in', 'people who sign in'],
  ['phoneLinks', 'linked WhatsApp phone', 'linked WhatsApp phones'],
  ['tvs', 'TV', 'TVs'],
  ['assistantNotes', 'assistant note', 'assistant notes'],
];
/** Kept only when some are: demo accounts a kept row names, out of the company (review M6). */
const KEPT_IF_ANY: [keyof TestDataDto['keep'], string, string][] = [
  ['demoStaffWithoutAccess', 'demo staff account, without access: a TV, note or site word names it', 'demo staff accounts, without access: a TV, note or site word names them'],
];
const counted = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/**
 * Settings → Test data (release 2.1): while the company is on trial, the test data the operator seeded
 * (sample products, demo staff and their history) is cleared here, once, by an admin, and that makes
 * the company live (`liveSince`, the API's word; the page never says "live", since the pilot's own
 * go-live comes long after). The menu offers it only then; afterwards the page says when it was
 * cleared and asks for nothing.
 */
export function SettingsGoLive() {
  const me = useMe().data;
  const orgQuery = useOrg();
  const org = orgQuery.data;
  const qc = useQueryClient();
  const nameId = useId();
  const admin = me ? can(me.role, 'settings') : false;
  const trial = org?.liveSince === null;
  const data = useQuery({ queryKey: qk.testData, queryFn: () => api.get<TestDataDto>('/org/test-data'), enabled: admin && trial });
  const [asking, setAsking] = useState(false);
  const [typed, setTyped] = useState('');
  const [done, setDone] = useState<TestDataClearedDto | null>(null);
  const clear = useMutation({
    mutationFn: (confirm: string) => api.post<TestDataClearedDto>('/org/test-data/clear', { confirm }),
    onSuccess: (r) => {
      setDone(r);
      setAsking(false);
      // Every list the company had is out of date now: all of it is asked for again.
      void qc.invalidateQueries();
    },
  });

  if (!me || !org) return <Spinner label="Loading" />;
  if (!admin) return <Empty title="Admins only" description="Clearing the test data is for admins." />;
  if (done)
    return (
      <div className={`panel ${s.glPanel}`} role="status">
        <h2 className={s.glTitle}>Test data cleared</h2>
        <p className={s.glText}>The test data and the sample products are gone, and this page is gone from Settings. Next, import your real SKU list.</p>
        <Link className="btn primary" to="/office/settings/import">
          Import SKUs
        </Link>
      </div>
    );
  if (!trial) return <Empty title="No test data" description={`The test data was cleared on ${zonedDateTime(org.liveSince!, org.timezone)}.`} />;
  if (data.isPending) return <Spinner label="Counting the test data" />;
  if (data.isError)
    return (
      <div className={`panel ${s.glPanel}`}>
        <ErrorNote error={data.error} />
        <button type="button" className="btn" onClick={() => void data.refetch()}>
          Try again
        </button>
      </div>
    );

  const removed = REMOVED.filter(([k]) => data.data.remove[k] > 0);
  const others = data.data.remove.otherProducts;
  const named = data.data.otherProductList.map((p) => p.code);
  const unnamed = others - named.length;
  const confirm = (e: FormEvent) => {
    e.preventDefault();
    if (typed === org.name) clear.mutate(typed);
  };
  return (
    <div className={s.glPage}>
      <div className={`panel ${s.glPanel}`}>
        <h2 className={s.glTitle}>Clear the test data</h2>
        <p className={s.glWarn}>This removes the test data and the sample products for good. Import your real SKU list after this.</p>
        {others > 0 && (
          <section className="callout" aria-label="Products that are not samples">
            <span className="ic">
              <Icon name="alert" />
            </span>
            <span>
              <b>{`${counted(others, 'product here is not a sample', 'products here are not samples')}: `}</b>
              {`${named.join(', ')}${unnamed > 0 ? ` and ${unnamed} more` : ''}. `}
              Clearing removes them too, with every box, order and count that uses them. Import them again after the clear.
            </span>
          </section>
        )}
        <div className={s.glCols}>
          <section aria-label="What is removed">
            <h3 className="caps">Removed</h3>
            {removed.length ? (
              <ul className={s.glList}>
                {removed.map(([k, one, many]) => (
                  <li key={k}>{counted(data.data.remove[k], one, many)}</li>
                ))}
              </ul>
            ) : (
              <p className={s.glText}>There is no test data. Clearing it only takes this page away.</p>
            )}
          </section>
          <section aria-label="What stays">
            <h3 className="caps">Stays</h3>
            <ul className={s.glList}>
              {KEPT.map(([k, one, many]) => (
                <li key={k}>{counted(data.data.keep[k], one, many)}</li>
              ))}
              {KEPT_IF_ANY.filter(([k]) => data.data.keep[k] > 0).map(([k, one, many]) => (
                <li key={k}>{counted(data.data.keep[k], one, many)}</li>
              ))}
              <li>Google Sheets and WhatsApp settings</li>
              <li>Sticker and order numbers, which carry on from where they are</li>
            </ul>
          </section>
        </div>
        <div>
          <button type="button" className="btn primary" onClick={() => setAsking(true)}>
            Clear test data…
          </button>
        </div>
      </div>
      <Modal
        open={asking}
        onOpenChange={(open) => {
          if (open) return;
          setAsking(false);
          setTyped('');
          clear.reset();
        }}
        title="Clear the test data?"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setAsking(false)}>
              Cancel
            </button>
            <button type="submit" form="test-data-confirm" className="btn primary" disabled={typed !== org.name || clear.isPending}>
              Clear test data
            </button>
          </>
        }
      >
        <form id="test-data-confirm" className={s.glForm} onSubmit={confirm}>
          <p className={s.glText}>
            {others > 0
              ? `It removes the sample products, the ${counted(others, 'other product', 'other products')} named on the page, and every box, `
              : 'It removes the sample products and every box, '}
            sticker batch, pallet, order, transfer, count and scan, with their history, the assistant’s conversations and product words, and the demo
            staff.
          </p>
          <p className={s.glText}>Your sites, users, devices, and the Google Sheets and WhatsApp connections stay. Afterwards, this page is gone from Settings.</p>
          <p className={s.glText}>
            This can’t be undone. Type <strong>{org.name}</strong> to confirm.
          </p>
          <label htmlFor={nameId}>Company name</label>
          {/* As typed: a phone keyboard's capitals, corrections or trailing space would never match (review M10). */}
          <input
            id={nameId}
            className="inp"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          {typed !== '' && typed !== org.name && <p className={s.glText}>Not yet: type it exactly as shown, with the same capitals and spaces.</p>}
          <ErrorNote error={clear.error} />
        </form>
      </Modal>
    </div>
  );
}
